// classificationHelpers.js
// Helper functions for working with classification levels in ProseMirror nodes

import {CLASSIFICATION_LEVELS, getClassificationByValue} from './classificationLevels';

/**
 * Set classification level on a paragraph or heading node
 * @param {Transaction} tr - ProseMirror transaction
 * @param {number} pos - Position of the node
 * @param {string} classificationLevel - Classification level value ('U', 'S', 'TS')
 * @returns {Transaction} - Modified transaction
 */
export function setNodeClassificationLevel(tr, pos, classificationLevel) {
    const node = tr.doc.nodeAt(pos);

    if (!node || (node.type.name !== 'paragraph' && node.type.name !== 'heading')) {
        console.warn('Can only set classification level on paragraph or heading nodes');
        return tr;
    }

    // Validate classification level
    if (classificationLevel && !getClassificationByValue(classificationLevel)) {
        console.warn(`Invalid classification level: ${classificationLevel}`);
        return tr;
    }

    return tr.setNodeMarkup(pos, null, {
        ...node.attrs,
        classificationLevel
    });
}

/**
 * Record the user's explicit Classification-menu choice as the block's "manual baseline" —
 * distinct from `classificationLevel` (the effective, possibly citation-raised level). Only
 * classificationGutterPlugin.js's menu-apply callback should call this; syncing the floor from
 * a citation change must never touch it (see referenceClassificationFloor.js).
 *
 * Persisting this on the node itself (rather than caching it only in memory) is what lets a
 * manual choice survive a reload: referenceClassificationFloor.js reads this attr directly, so
 * without persisting it there'd be no way to tell "the user chose C and a citation later raised
 * it to TS" apart from "the user chose TS", and removing the citation after a reload could
 * never fall back below TS.
 *
 * @param {Transaction} tr - ProseMirror transaction
 * @param {number} pos - Position of the node
 * @param {string} classificationLevel - The level explicitly chosen via the Classification menu
 * @returns {Transaction} - Modified transaction
 */
export function setNodeManualClassificationBaseline(tr, pos, classificationLevel) {
    const node = tr.doc.nodeAt(pos);

    if (!node || (node.type.name !== 'paragraph' && node.type.name !== 'heading')) {
        console.warn('Can only set classification baseline on paragraph or heading nodes');
        return tr;
    }

    if (classificationLevel && !getClassificationByValue(classificationLevel)) {
        console.warn(`Invalid classification level: ${classificationLevel}`);
        return tr;
    }

    return tr.setNodeMarkup(pos, null, {
        ...node.attrs,
        manualClassificationLevel: classificationLevel
    });
}

/**
 * Set distribution and compartment metadata on a paragraph or heading node
 * @param {Transaction} tr - ProseMirror transaction
 * @param {number} pos - Position of the node
 * @param {Object} metadata - Metadata object containing distribution and compartment
 * @returns {Transaction} - Modified transaction
 */
export function setNodeClassificationMetadata(tr, pos, {distribution, compartment}) {
    const node = tr.doc.nodeAt(pos);
    if (!node || (node.type.name !== 'paragraph' && node.type.name !== 'heading')) {
        console.warn('Can only set classification metadata on paragraph or heading nodes');
        return tr;
    }

    return tr.setNodeMarkup(pos, null, {
        ...node.attrs,
        distribution: Array.isArray(distribution) ? distribution : node.attrs.distribution,
        compartment: Array.isArray(compartment) ? compartment : node.attrs.compartment,
    });
}

/**
 * Get classification level from a node
 * @param {Node} node - ProseMirror node
 * @returns {string} - Classification level (defaults to 'U')
 */
export function getNodeClassificationLevel(node) {
    if (node.type.name !== 'paragraph' && node.type.name !== 'heading') {
        return 'U';
    }
    return node.attrs.classificationLevel || 'U';
}

/**
 * Get classification info (full object) from a node
 * @param {Node} node - ProseMirror node
 * @returns {Object|null} - Classification level object or null
 */
export function getNodeClassificationInfo(node) {
    const level = getNodeClassificationLevel(node);
    return level ? getClassificationByValue(level) : null;
}

/**
 * Get all available classification levels
 * @returns {Array} - Array of classification level objects
 */
export function getClassificationLevels() {
    return CLASSIFICATION_LEVELS;
}

/**
 * Apply classification level styling to a DOM element
 * @param {HTMLElement} element - DOM element to style
 * @param {string} classificationLevel - Classification level value
 */
export function applyClassificationStyling(element, classificationLevel) {
    const info = getClassificationByValue(classificationLevel);
    if (!info) return;

    element.style.borderLeft = `4px solid ${info.color}`;
    element.style.backgroundColor = info.bgColor;
    element.style.paddingLeft = '12px';
    element.setAttribute('data-classification-level', classificationLevel);
    element.setAttribute('title', info.label);
}

/**
 * Remove classification level styling from a DOM element
 * @param {HTMLElement} element - DOM element to unstyle
 */
export function removeClassificationStyling(element) {
    element.style.borderLeft = '';
    element.style.backgroundColor = '';
    element.style.paddingLeft = '';
    element.removeAttribute('data-classification-level');
    element.removeAttribute('title');
}

/**
 * Set classification on a figure node.
 * Because figure is now a single atom node, only one setNodeMarkup call is needed.
 * The caption always inherits classification from the parent figure by design.
 *
 * @param {Transaction} tr - ProseMirror transaction (mutated in place)
 * @param {number} figurePos - Position of the figure node
 * @param {Object} params
 * @param {string} params.classificationLevel - e.g. 'U', 'S', 'TS'
 * @param {string[]} params.distribution
 * @param {string[]} params.compartment
 * @returns {Transaction} - The same (mutated) transaction
 */
export function setFigureClassification(tr, figurePos, {classificationLevel, distribution, compartment}) {
    const figureNode = tr.doc.nodeAt(figurePos);
    if (!figureNode || figureNode.type.name !== 'figure') {
        console.warn('setFigureClassification: expected a figure node at pos', figurePos);
        return tr;
    }
    return tr.setNodeMarkup(figurePos, null, {
        ...figureNode.attrs,
        classificationLevel,
        distribution: distribution || [],
        compartment: compartment || [],
    });
}
